import { Observer } from "mobx-react";
import { AssignedWorkflowInstance } from "models/Workflows/WorkflowInstance";
import React from "react";
import { Answer } from "../../../../models/Answer";
import { AnswerTypeNames } from "../../../../models/AnswerType";
import { ThemeColors } from "../../../../Theme/AppTheme";
import hexToRGB from "../../../../utils/hexToRGB";
import { buildQuestionVisibilityMap } from "../../../../utils/questionDependencyRendering";
import {
    EvalLicensedModule,
    EvalQuestion,
    EvalStore,
} from "../../Stores/EvalStore";
import ScaledResponseAnswer from "./ScaledResponseAnswer";
import StarRatingAnswer from "./StarRatingAnswer";
import TagOrFillInResponseAnswer from "./TagOrFillInResponseAnswer";

interface OwnProps {
    module?: EvalLicensedModule;
    store: EvalStore;
    standalone?: boolean;
    isEditable?: boolean;
}

type Props = OwnProps;

export function buildEvaluationQuestionVisibilityMap(
    questions: EvalQuestion[],
    answers: Answer[] | undefined,
): Map<string, boolean> {
    return buildQuestionVisibilityMap({
        questions,
        answers,
        getModuleInstanceIdForQuestion: (question) =>
            question.evaluationModuleId,
        getModuleInstanceIdForAnswer: (answer) => answer.evaluationModuleId,
    });
}

const QuestionAnswerList = (props: Props) => {
    return (
        <Observer>
            {() => {
                const questionsWithDependencies =
                    props.module?.questions.filter(
                        (r) => r.dependencies?.conditions.length,
                    );

                const moduleQuestions = (props.module?.sortedQuestion ?? []).map(
                    (q) => {
                        var evalQuestion = q as EvalQuestion;
                        evalQuestion.evaluationModuleId = props.module
                            ?.evaluationModuleId as string;
                        return evalQuestion;
                    },
                );
                const visibilityMap = buildEvaluationQuestionVisibilityMap(
                    moduleQuestions,
                    props.store.currentEval?.answers,
                );

                const sortedQuestions = moduleQuestions.filter(
                    (question) => visibilityMap.get(question.id) ?? false,
                );

                const moduleUIModel = props.store.getModuleUIModel(
                    props.module?.evaluationModuleId,
                );

                return (
                    <>
                        {props.module?.questions && (
                            <>
                                {sortedQuestions?.map((value, index) => {
                                    let isEditable =
                                        !props.store.hasInProgressWorkflows &&
                                        props.store.currentEval?.isEditable;

                                    if (props.module?.isWorkflowModule) {
                                        const assignedToUser =
                                            props.store?.currentEval?.workflowInstances?.some(
                                                (instance) =>
                                                    (
                                                        instance as AssignedWorkflowInstance
                                                    ).assignedToUser &&
                                                    instance.isActive,
                                            );
                                        isEditable = assignedToUser;
                                    }

                                    const answer =
                                        props.store.currentEval?.getAnswerForQuestion(
                                            value,
                                            moduleUIModel?.licensedModule
                                                .evaluationModuleId,
                                        );
                                    if (answer?.isDisputed) {
                                        if (
                                            !props.store.currentEval
                                                ?.canChangeDisputedAnswers
                                        ) {
                                            //answer is disputed, but current user cannot change disputed question
                                            //force answer to be readonly / not editable
                                            isEditable = false;
                                        }
                                    }

                                    if (value.isActive) {
                                        const leftBorder =
                                            value.parentId &&
                                            questionsWithDependencies?.includes(
                                                value,
                                            )
                                                ? hexToRGB(
                                                      ThemeColors.purple,
                                                      1,
                                                  )
                                                : questionsWithDependencies?.includes(
                                                      value,
                                                  )
                                                ? hexToRGB(
                                                      ThemeColors.red,
                                                      0.75,
                                                  )
                                                : value.parentId
                                                ? hexToRGB(
                                                      ThemeColors.blue,
                                                      0.5,
                                                  )
                                                : undefined;

                                        if (
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.ScaledResponse ||
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.ScaledBooleanResponse ||
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.QuestionGrouping
                                        ) {
                                            return (
                                                <ScaledResponseAnswer
                                                    moduleUIModel={
                                                        moduleUIModel
                                                    }
                                                    answerDisputes={
                                                        answer?.answerDisputes
                                                    }
                                                    question={value}
                                                    standalone={
                                                        props.standalone
                                                    }
                                                    leftBorderColor={leftBorder}
                                                    key={value.id}
                                                    isDisabled={
                                                        props.isEditable !==
                                                        undefined
                                                            ? !props.isEditable
                                                            : !isEditable
                                                    }
                                                    showDisputeCheckbox={
                                                        props.store
                                                            .disputeHasStarted
                                                    }
                                                />
                                            );
                                        } else if (
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.TagResponse ||
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.ScoredTagResponse ||
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.TextResponse ||
                                            value.answerType.answerTypeName ===
                                                AnswerTypeNames.DateResponse
                                        ) {
                                            return (
                                                <TagOrFillInResponseAnswer
                                                    key={value.id}
                                                    standalone={
                                                        props.standalone
                                                    }
                                                    leftBorderColor={leftBorder}
                                                    moduleUIModel={
                                                        moduleUIModel
                                                    }
                                                    question={value}
                                                    isDisabled={
                                                        props.isEditable !==
                                                        undefined
                                                            ? !props.isEditable
                                                            : !isEditable
                                                    }
                                                />
                                            );
                                        } else if (
                                            value.answerType.answerTypeName ===
                                            AnswerTypeNames.StarRating
                                        ) {
                                            return (
                                                <StarRatingAnswer
                                                    key={value.id}
                                                    standalone={
                                                        props.standalone
                                                    }
                                                    moduleUIModel={
                                                        moduleUIModel
                                                    }
                                                    question={value}
                                                    leftBorderColor={leftBorder}
                                                    isDisabled={
                                                        props.isEditable !==
                                                        undefined
                                                            ? !props.isEditable
                                                            : !isEditable
                                                    }
                                                    showDisputeCheckbox={
                                                        props.store
                                                            .disputeHasStarted
                                                    }
                                                />
                                            );
                                        } else {
                                            return "";
                                        }
                                    } else {
                                        return "";
                                    }
                                })}
                            </>
                        )}
                    </>
                );
            }}
        </Observer>
    );
};

export default QuestionAnswerList;
